import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { GetUserProfileDetails, UpdateUserProfile } from '../../../network/User';
import { useAuth } from '../../customContextProviders/AuthContext';

export default function Profile({ setAlertMetas, alertMetas }) {
  const { sessionUserData } = useAuth();

  const fileInputRef = useRef(null);

  const [listDense, setListDense] = useState(false);
  const [listSecondary, setListSecondary] = useState(false);

  const [userProfileData, setUserProfileData] = useState();

  const [userDetail, setUserDetail] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    image: null
  });

  const handleEditClick = () => {
    fileInputRef.current.click();
  };
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64String = e.target.result.split(',')[1];
        setUserDetail((prevUserDetail) => ({
          ...prevUserDetail,
          image: base64String,
          imagePreview: URL.createObjectURL(file)
        }));
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
      };

      reader.readAsDataURL(file);
    } else {
      console.error('No file selected!');
    }
  };

  const handleDeleteClick = () => {
    setUserDetail((prevUserDetail) => ({
      ...prevUserDetail,
      image: null,
      imagePreview: null
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetail({ ...userDetail, [name]: value });
  };

  useEffect(() => {
    if (userDetail.image) {
      const dataUrl = `data:image/png;base64,${userDetail.image}`;
      setUserDetail((prevUserDetail) => ({
        ...prevUserDetail,
        imagePreview: dataUrl // Set the preview from the Base64 string
      }));
    }
  }, [userDetail.image]);

  const updateProfile = async () => {
    const payload = {
      ...userDetail,
      image: userDetail.image
    };

    try {
      const res = await UpdateUserProfile(sessionUserData?.user?.id, payload);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'success',
          snackBarOpened: true,
          msg: 'User Updated Successfully!'
        });
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message || 'Failed to update user!'
      });
    } finally {
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  const getUserProfileData = async () => {
    try {
      const res = await GetUserProfileDetails(sessionUserData?.user?.id);
      setUserProfileData(res?.data?.data);

      if (res?.data?.data?.image) {
        // Decode base64 to Blob and set imagePreview
        const byteCharacters = atob(res.data.data.image); // Decode Base64 to binary string
        const byteNumbers = Array.from(byteCharacters).map((char) => char.charCodeAt(0)); // Convert binary string to byte array
        const byteArray = new Uint8Array(byteNumbers); // Create Uint8Array
        const blob = new Blob([byteArray], { type: 'image/png' }); // Create Blob
        const imageUrl = URL.createObjectURL(blob); // Generate preview URL

        // Update image and preview in state
        setUserDetail((prevUserDetail) => ({
          ...prevUserDetail,
          image: res.data.data.image,
          imagePreview: imageUrl
        }));
      } else {
        // Clear image preview if no image
        setUserDetail((prevUserDetail) => ({
          ...prevUserDetail,
          image: null,
          imagePreview: null
        }));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (userProfileData) {
      setUserDetail({
        name: userProfileData.name,
        email: userProfileData.email,
        password: userProfileData.password,
        phone: userProfileData.phone,
        image: userProfileData.image || null,
        imagePreview: userProfileData.image
          ? `data:image/png;base64,${userProfileData.image}`
          : null
      });
    }
  }, [userProfileData]);

  useEffect(() => {
    if (sessionUserData?.user?.id) {
      getUserProfileData();
    }
  }, [sessionUserData?.user?.id]);

  return (
    <>
      <List dense={listDense} sx={{ p: 0 }}>
        <ListItem
          sx={{ px: 2, py: 3, backgroundColor: 'rgba(176, 185, 199, .1)' }}
          secondaryAction={
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={{ xs: 1, sm: 2 }}
            >
              <IconButton edge="end" aria-label="profile-picture">
                <Avatar src={userDetail?.imagePreview} alt="User profile" />
              </IconButton>
              <IconButton edge="end" aria-label="edit" onClick={handleEditClick}>
                <EditOutlinedIcon />
              </IconButton>
              <IconButton edge="end" aria-label="delete" onClick={handleDeleteClick}>
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Stack>
          }
        >
          <ListItemText
            sx={{ color: '#535353' }}
            primary="Profile Picture"
            listSecondary={listSecondary ? 'Secondary text' : null}
            primaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
            secondaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
          />
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </ListItem>

        <ListItem
          sx={{ px: 2, py: 3, backgroundColor: 'white' }}
          secondaryAction={
            <TextField
              size="small"
              variant="outlined"
              label="Name"
              name="name"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '12px', sm: '14px' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '12px', sm: '14px' }
                }
              }}
              value={userDetail.name}
              onChange={handleChange}
            />
          }
        >
          <ListItemText
            primary=" Name"
            listSecondary={listSecondary ? 'Secondary text' : null}
            primaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
            secondaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
          />
        </ListItem>

        <ListItem
          sx={{ px: 2, py: 3, backgroundColor: 'rgba(176, 185, 199, .1)' }}
          secondaryAction={
            <TextField
              size="small"
              variant="outlined"
              label="Mobile"
              name="phone"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '12px', sm: '14px' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '12px', sm: '14px' }
                }
              }}
              value={userDetail.phone}
              onChange={handleChange}
            />
          }
        >
          <ListItemText
            primary="Mobile"
            listSecondary={listSecondary ? 'Secondary text' : null}
            primaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
            secondaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
          />
        </ListItem>

        <ListItem
          sx={{ px: 2, py: 3, backgroundColor: 'white' }}
          secondaryAction={
            <TextField
              size="small"
              variant="outlined"
              label="Email "
              name="email"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '12px', sm: '14px' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '12px', sm: '14px' }
                }
              }}
              value={userDetail.email}
              disabled
              onChange={handleChange}
            />
          }
        >
          <ListItemText
            primary="Email"
            listSecondary={listSecondary ? 'Secondary text' : null}
            primaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
            secondaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
          />
        </ListItem>

        <ListItem
          sx={{ px: 2, py: 3, backgroundColor: 'rgba(176, 185, 199, .1)' }}
          secondaryAction={
            <TextField
              size="small"
              variant="outlined"
              label="Password"
              name="password"
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '12px', sm: '14px' }
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '12px', sm: '14px' }
                }
              }}
              value={userDetail.password}
              onChange={handleChange}
            />
          }
        >
          <ListItemText
            primary="Password "
            listSecondary={listSecondary ? 'Secondary text' : null}
            primaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
            secondaryTypographyProps={{ fontSize: { xs: '12px', sm: '14px' } }}
          />
        </ListItem>
      </List>

      <Box sx={{ width: '100%', p: 3, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          sx={{ boxShadow: 'none' }}
          onClick={updateProfile}
        >
          SAVE
        </Button>
      </Box>
    </>
  );
}
